<script setup>

    import { onMounted, ref } from 'vue'

    import { useCaregiverSingleStore, useNotificationStore, useViewStore } from '@/Stores'
    import { storeToRefs } from 'pinia'

    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'

    import { useRoute } from 'vue-router'
    import { useJobRunner } from '@/Helpers/jobRunner'
    import LatteCaregiverCapabilities from '@/Components/Caregiver/LatteCaregiverCapabilities.vue'

    const caregiverSingleStore = useCaregiverSingleStore()
    const notificationStore = useNotificationStore()
    const viewStore = useViewStore()

    const { caregiver } = storeToRefs(caregiverSingleStore)
    const { isEmbedded } = storeToRefs(viewStore)

    const caregiverJobRunner = useJobRunner('caregiver')
    caregiverJobRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        caregiverSingleStore.stopInactivityTimeout()
    })
    caregiverJobRunner.subscribe('finishedJobSuccess', function() {
        caregiverSingleStore.resetInactivityRefreshTimer()
    })
    caregiverJobRunner.subscribe('addedJob', function() {
        caregiverSingleStore.resetInactivityRefreshTimer()
        notificationStore.removeById(caregiverSingleStore.getMaxRetryDialogId())
        caregiverSingleStore.removeMaxRetryDialogId()
    })
    caregiverJobRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        caregiverSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    })

    function cancelCaregiverUpdate(caregiverId) {
        caregiverJobRunner.removeJob('update', caregiverId)
    }

    const route = useRoute()

    async function hydrateCaregiver(data) {
        await caregiverSingleStore.hydrate(data)
    }

    function updateCaregiver(data, formId) {
        caregiverSingleStore.applyLocalChangeToActiveCaregiver(data)
        caregiverJobRunner.addJob('update', caregiver.value.id, data, [formId], async (data, formId, jobId) => {
            await caregiverSingleStore.updateCaregiver(caregiver.value.id, data, formId, jobId)
                .then(function (response) {
                    if (caregiverJobRunner.hasScheduleJob('update', caregiver.value.id) == false) {
                        hydrateCaregiver(response.data)
                    }
                    caregiverJobRunner.handleJobSuccess('update', caregiver.value.id, jobId)
                    notificationStore.add('Changes Saved')
                })
                .catch(function(error) {
                    caregiverJobRunner.handleJobError('update', caregiver.value.id, jobId, error.error.response?.status)
                })
        })
    }

    onMounted(async () => {
        const caregiver_id = route.params.coffee_caregiver_user_profile_id
        caregiverSingleStore.getCaregiverByCoffeeUserProfileId(caregiver_id)
            .then(response => {
                caregiverSingleStore.hydrate(response.data)
            })
    })

</script>

<template>

    <div
        class="space-y-8"
    >
        <div class="mx-auto max-w-5xl">

            <LatteStyledContainer
                id="caregiver_capabilities"
                title="Experience & Capabilities"
                :isEmbedded="isEmbedded"
                :forceSingleLineMobileLayout="true"
            >
                <template #default>
                    <LatteCaregiverCapabilities
                        @caregiver:update="updateCaregiver"
                        @caregiver:cancel-update="cancelCaregiverUpdate"
                    />
                </template>
            </LatteStyledContainer>

        </div>

    </div>

</template>
